<script>
  import { timeout } from "overline";
  import { darken } from "drawing";
  import isEqual from "lodash/isEqual";
  import { createEventDispatcher, tick } from "svelte";
  import CheckIcon from "@local/assets/icons/check.svg";
  import ColorWheelIcon from "@local/assets/icons/color-wheel.svg";
  import XIcon from "@local/assets/icons/x.svg";
  import XIconSm from "@local/assets/icons/x-sm.svg";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";
  import { clickOutside, Popover } from "svelte-utilities";

  export let border = false;
  export let options = [];
  /** @type {string | null} */
  export let label = "Label";
  export let value;
  /** @type {string | null} */
  export let labelWidth = null;
  export let disabled = false;
  export let readonly = false;
  /** @type {string | null} */
  export let placeholder = null;
  export let outerBorder = true;
  export let showCaret = false;
  export let deletable = false;
  export let bg = true;

  const dispatch = createEventDispatcher();

  let containers = [];
  let input;
  let textInput;
  let popover;
  let focused = false;
  let showOptions = false;
  let selectedOption = null;
  let textString = "";

  $: lw = labelWidth ? `width:${labelWidth};` : "";
  $: filteredOptions = options.filter((o) => {
    if (!textString) return true;
    return o.value.toLowerCase().includes(textString.toLowerCase());
  });
  $: optionsByValue = options.reduce((acc, o) => {
    acc[o.value] = o;
    return acc;
  }, {});
  $: userOptions = Array.isArray(value)
    ? value.filter((v) => !optionsByValue[v]).map((v) => ({ label: v, value: v }))
    : [];
  $: allOptions = [...options, ...userOptions];
  $: visibleOptions = [
    ...filteredOptions,
    ...userOptions,
    ...(textString ? [{ label: textString, value: textString, cta: "Type enter to add" }] : []),
  ];
  $: currentlySelected = Array.isArray(value)
    ? value.reduce((acc, val) => {
        acc[val] = true;
        return acc;
      }, {})
    : {};
  $: containers = [input, popover];

  async function focus(e) {
    if (disabled) return;
    focused = true;
    // input.focus();
    await tick();
    textInput.focus();
    dispatch("focus", e);
    selectedOption = null;
    showOptions = true;
  }

  function inputFocus() {
    focused = true;
    showOptions = true;
  }

  function chooseOption(option) {
    textString = "";
    if (!option) {
      return blur();
    }
    const index = allOptions.findIndex((o) => o.value === option.value);
    if (index > -1) {
      selectedOption = index;
      const v = currentlySelected[option.value] ? false : true;
      if (v) {
        const beforeSelection = Array.isArray(value) ? [...value] : [];
        const selected = beforeSelection.reduce(
          (acc, val) => {
            acc[val] = true;
            return acc;
          },
          { [option.value]: v },
        );
        value = allOptions.map((o) => o.value).filter((v) => selected[v]);
      } else {
        const selected = Array.isArray(value)
          ? value.reduce((acc, val) => {
              if (val !== option.value) acc[val] = true;
              return acc;
            }, {})
          : {};
        value = allOptions.map((o) => o.value).filter((v) => selected[v]);
      }
      dispatch("input", { value });
    } else {
      // New option
      value = Array.isArray(value) ? [...value, option.value] : [option.value];
      dispatch("input", { value });
    }
    // dispatch("mouseout");
    // blur();
  }

  function keydown(evt) {
    switch (evt.key) {
      case "Tab":
        textInput.blur();
        blur();
        break;
      case "Enter":
        if (!showOptions) {
          selectedOption = null;
          showOptions = true;
        } else if (visibleOptions[selectedOption] != undefined) {
          chooseOption(visibleOptions[selectedOption]);
        } else if (textString.trim().length > 0) {
          chooseOption({ label: textString.trim(), value: textString.trim() });
        }
        break;
      case "ArrowDown":
        evt.preventDefault();
        showOptions = true;
        if (selectedOption === null) {
          selectedOption = 0;
        } else if (visibleOptions.length > selectedOption + 1) {
          selectedOption += 1;
        }
        break;
      case "ArrowUp":
        evt.preventDefault();
        if (selectedOption > 0) {
          selectedOption -= 1;
        }
        break;
    }
  }

  function deleteOption(option) {
    dispatch("delete-option", { value: option.value });
  }

  function handleInput(e) {
    keydown(e);
  }

  function handleBlur(e) {
    // blur();
  }

  async function blur(e) {
    if (e && e.type === "blur") {
      await timeout();

      if (popover && popover.contains(document.activeElement)) {
        return;
      }
    } else if (e && e.type === "outclick") {
      if (popover && popover.contains(document.activeElement)) {
        return;
      }
    }

    focused = false;
    if (input) input.blur();
    showOptions = false;
  }
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-mouse-events-have-key-events -->
<div
  class="prop-container"
  class:bg-white={bg}
  class:has-outer-border={outerBorder}
  class:has-inner-border={!outerBorder}
  class:focused
  disabled={readonly || disabled}
  class:readonly={readonly || disabled}
  class:visible-border={border}
  on:click={focus}
  tabindex={disabled || readonly ? -1 : 0}
  bind:this={input}
  on:mouseover
  on:mouseout
  on:keydown={keydown}
  on:focus={focus}
  use:clickOutside
  on:outclick={blur}>
  {#if label !== null}
    <div style={lw} class="flex-none">
      <div class="label">
        <slot name="label">
          {label}
        </slot>
      </div>
    </div>
  {/if}
  <div class="input-div" class:no-label={label === null}>
    <div class="input" class:disabled>
      {#if focused && !disabled}
        <div class="w-full flex">
          <input
            size="1"
            bind:this={textInput}
            {placeholder}
            bind:value={textString}
            on:focus={inputFocus}
            on:input={handleInput}
            on:blur={handleBlur} />
        </div>
      {:else if value === "Mixed"}
        Mixed
      {:else if value?.length}
        <slot name="selected" {value}>
          <div class="flex gap-1 items-center flex-wrap">
            {#each value as option}
              <div class="flex gap-1 bg-gray-200 rounded px-1.5">
                <div>{option}</div>
                {#if !disabled}
                  <button
                    class="text-gray-500 hover:text-black"
                    tabindex="-1"
                    on:click|stopPropagation={() => chooseOption({ value: option })}>
                    <XIconSm />
                  </button>
                {/if}
              </div>
            {/each}
          </div>
        </slot>
      {:else}
        <span class="text-gray-400">{placeholder || ""}</span>
      {/if}
    </div>
    {#if !disabled}
      <div class="flex-none caret-down-icon" class:show-caret={showCaret}>
        <CaretDownIcon />
      </div>
    {/if}
  </div>
  {#if showOptions}
    <Popover container={input} fit>
      <div class="w-full py-1 text-xs overflow-hidden select-input-popover" bind:this={popover} tabindex="0">
        {#each visibleOptions as option, index}
          <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
          <li
            class="option-item flex p-1 hover:bg-gray-100 items-center cursor-pointer"
            class:selected={index === selectedOption}
            on:mousedown|stopPropagation
            on:keydown={() => chooseOption(option)}
            on:click|stopPropagation={() => chooseOption(option)}>
            <div class="flex-none w-6">
              {#if currentlySelected[option.value]}
                <CheckIcon />
              {/if}
            </div>
            <slot name="option" {option}>
              <div class="flex gap-2 items-center">
                {#if option.color !== undefined}
                  {#if option.color === "rainbow"}
                    <div>
                      <ColorWheelIcon />
                    </div>
                  {:else}
                    <div
                      class="w-4 h-4 rounded"
                      class:no-color-bg={!option.color || option.color === "Mixed"}
                      style="
                      background-color: {option.color};
                      border: 1px solid {darken(-0.5, option.color)};
                    " />
                  {/if}
                {:else if option.icon !== undefined}
                  <svelte:component this={option.icon} />
                {/if}
                <div class="truncate">{option.label}</div>
                {#if option.cta}
                  <div class="text-gray-400 italic text-xs">{option.cta}</div>
                {/if}
              </div>
            </slot>
            {#if deletable && option.deletable !== false}
              <button
                class="ml-auto delete-button rounded hover:bg-gray-200"
                on:click|stopPropagation={() => deleteOption(option)}>
                <XIcon />
              </button>
            {/if}
          </li>
        {/each}
      </div>
    </Popover>
  {/if}
</div>

<style lang="scss">
  input {
    border: none;
    background-color: none;
    outline: none;
    min-width: 0;
    max-width: 100%;

    @apply grow shrink bg-transparent;
  }

  .prop-container {
    @apply w-full flex items-center rounded relative overflow-hidden;
    outline: none;

    .input-div {
      @apply relative grow truncate flex items-center;

      &:not(.no-label) {
        @apply ml-2;
      }

      &.no-label {
        @apply ml-0;
      }
    }

    .caret-down-icon:not(.show-caret) {
      visibility: hidden;
    }

    &.has-outer-border {
      @apply p-2;

      &:hover:not(.readonly):not(.focused),
      &.visible-border {
        @apply ring-1 ring-inset ring-gray-300;
      }
      &.focused {
        @apply ring-2 ring-inset ring-blue-500;
      }

      .caret-down-icon {
        visibility: visible;
      }
    }

    &.has-inner-border {
      .input-div {
        @apply p-1 rounded;
      }

      &:hover {
        .caret-down-icon {
          visibility: visible;
        }
      }
    }

    &.has-inner-border:not(.readonly):not(.focused) {
      .input-div:hover {
        @apply ring-1 ring-inset ring-gray-300;
      }
    }

    .label {
      @apply truncate text-gray-400;
    }

    .input {
      @apply min-w-0 grow bg-transparent truncate;
    }
  }

  li.option-item.selected {
    @apply bg-blue-400 text-white;
  }

  .option-item {
    .delete-button {
      display: none;
    }
  }

  .option-item:hover {
    .delete-button {
      display: block;
    }
  }
</style>
